import { createHash, createHmac, randomBytes, timingSafeEqual } from "crypto";

const QUERY_TOKEN_PREFIX = "gencow_qs1";
const QUERY_KEY_SEPARATOR = "::";
const DEFAULT_TTL_SECONDS = 300;
const MAX_TTL_SECONDS = 3600;
const MAX_TOKEN_LENGTH = 8192;

export type RealtimeSubscriptionPolicy = "public" | "authenticated" | "authorized";

export type RealtimeQuerySubscriptionTokenFailure =
  | "malformed"
  | "bad_signature"
  | "app_mismatch"
  | "expired"
  | "invalid_payload";

export interface RealtimeQuerySubscriptionGrant {
  token: string;
  query: string;
  subscriptionKey: string;
  policy: RealtimeSubscriptionPolicy;
  userId: string;
  expiresAt: number;
  epoch?: string;
}

export interface CreateRealtimeQuerySubscriptionTokenInput {
  appName: string;
  query: string;
  subscriptionKey?: string;
  args?: unknown;
  scope?: unknown;
  scopeHash?: string;
  policy: RealtimeSubscriptionPolicy;
  userId: string;
  secret: string;
  ttlSeconds?: number;
  nowMs?: number;
  nonce?: string;
  epoch?: string | number;
}

export interface VerifyRealtimeQuerySubscriptionTokenInput {
  appName: string;
  secret: string;
  nowMs?: number;
}

export type VerifyRealtimeQuerySubscriptionTokenResult =
  | {
      ok: true;
      appName: string;
      query: string;
      subscriptionKey: string;
      policy: RealtimeSubscriptionPolicy;
      scopeHash: string;
      userId: string;
      epoch?: string;
      issuedAt: number;
      expiresAt: number;
    }
  | { ok: false; reason: RealtimeQuerySubscriptionTokenFailure };

function normalizeForStableJson(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => normalizeForStableJson(item));
  if (typeof value === "object") {
    const source = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      const normalized = normalizeForStableJson(source[key]);
      if (normalized !== undefined) sorted[key] = normalized;
    }
    return sorted;
  }
  return value;
}

function isEmptyPlainObject(value: unknown): boolean {
  return !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0;
}

function encodeBase64Url(value: string): string {
  return Buffer.from(value, "utf8").toString("base64url");
}

function decodeBase64Url(value: string): string | null {
  try {
    return Buffer.from(value, "base64url").toString("utf8");
  } catch {
    return null;
  }
}

function signSegment(segment: string, secret: string): string {
  return createHmac("sha256", secret).update(segment).digest("base64url");
}

function safeEqual(a: string, b: string): boolean {
  const left = Buffer.from(a);
  const right = Buffer.from(b);
  return left.length === right.length && timingSafeEqual(left, right);
}

function resolveTtlMs(ttlSeconds?: number): number {
  const ttl = Number.isFinite(ttlSeconds) && ttlSeconds! > 0 ? ttlSeconds! : DEFAULT_TTL_SECONDS;
  return Math.floor(Math.min(ttl, MAX_TTL_SECONDS) * 1000);
}

function randomNonce(): string {
  return randomBytes(16).toString("base64url");
}

function validateRequiredString(value: string, label: string): void {
  if (typeof value !== "string" || value.length === 0) throw new Error(`${label} required`);
}

function isRealtimeSubscriptionPolicy(value: unknown): value is RealtimeSubscriptionPolicy {
  return value === "public" || value === "authenticated" || value === "authorized";
}

export function normalizeRealtimeQuerySubscriptionEpoch(
  epoch: string | number | null | undefined,
): string | undefined {
  if (epoch === undefined || epoch === null) return undefined;
  if (typeof epoch === "number") {
    if (!Number.isFinite(epoch)) throw new Error("epoch must be finite");
    return String(epoch);
  }
  if (typeof epoch !== "string" || epoch.length === 0) {
    throw new Error("epoch must be a non-empty string");
  }
  return epoch;
}

export function buildRealtimeQuerySubscriptionKey(query: string, args?: unknown): string {
  validateRequiredString(query, "query");
  const normalized = normalizeForStableJson(args);
  if (normalized === undefined || isEmptyPlainObject(normalized)) return query;
  return `${query}${QUERY_KEY_SEPARATOR}${JSON.stringify(normalized)}`;
}

export function buildRealtimeQueryScopeHash(input: {
  query: string;
  subscriptionKey?: string;
  scope?: unknown;
}): string {
  validateRequiredString(input.query, "query");
  const normalizedScope =
    input.scope === undefined
      ? { query: input.query, subscriptionKey: input.subscriptionKey ?? input.query }
      : normalizeForStableJson(input.scope);
  return createHash("sha256").update(JSON.stringify(normalizedScope)).digest("base64url");
}

export function createRealtimeQuerySubscriptionToken(
  input: CreateRealtimeQuerySubscriptionTokenInput,
): string {
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.query, "query");
  validateRequiredString(input.userId, "userId");
  validateRequiredString(input.secret, "secret");
  if (!isRealtimeSubscriptionPolicy(input.policy)) throw new Error("policy required");

  const nowMs = input.nowMs ?? Date.now();
  const args = normalizeForStableJson(input.args);
  const subscriptionKey = input.subscriptionKey ?? buildRealtimeQuerySubscriptionKey(input.query, args);
  validateRequiredString(subscriptionKey, "subscriptionKey");
  const epoch = normalizeRealtimeQuerySubscriptionEpoch(input.epoch);
  const scopeHash =
    input.scopeHash ??
    buildRealtimeQueryScopeHash({
      query: input.query,
      subscriptionKey,
      scope: input.scope,
    });

  const payload = {
    v: 1,
    appName: input.appName,
    query: input.query,
    subscriptionKey,
    policy: input.policy,
    scopeHash,
    userId: input.userId,
    ...(epoch ? { epoch } : {}),
    iat: nowMs,
    exp: nowMs + resolveTtlMs(input.ttlSeconds),
    nonce: input.nonce ?? randomNonce(),
  };
  const payloadSegment = encodeBase64Url(JSON.stringify(payload));
  const signature = signSegment(payloadSegment, input.secret);
  return `${QUERY_TOKEN_PREFIX}.${payloadSegment}.${signature}`;
}

export function verifyRealtimeQuerySubscriptionToken(
  token: string,
  input: VerifyRealtimeQuerySubscriptionTokenInput,
): VerifyRealtimeQuerySubscriptionTokenResult {
  if (typeof token !== "string" || token.length === 0 || token.length > MAX_TOKEN_LENGTH) {
    return { ok: false, reason: "malformed" };
  }
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.secret, "secret");

  const parts = token.split(".");
  if (parts.length !== 3 || parts[0] !== QUERY_TOKEN_PREFIX) return { ok: false, reason: "malformed" };
  const [, payloadSegment, signature] = parts;
  if (!safeEqual(signature, signSegment(payloadSegment, input.secret))) {
    return { ok: false, reason: "bad_signature" };
  }

  const decoded = decodeBase64Url(payloadSegment);
  if (!decoded) return { ok: false, reason: "malformed" };

  let payload: unknown;
  try {
    payload = JSON.parse(decoded);
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (!payload || typeof payload !== "object") return { ok: false, reason: "invalid_payload" };
  const record = payload as Record<string, unknown>;

  if (
    record.v !== 1 ||
    typeof record.query !== "string" ||
    typeof record.subscriptionKey !== "string" ||
    typeof record.userId !== "string" ||
    typeof record.scopeHash !== "string" ||
    !isRealtimeSubscriptionPolicy(record.policy)
  ) {
    return { ok: false, reason: "invalid_payload" };
  }
  if (record.appName !== input.appName) return { ok: false, reason: "app_mismatch" };
  if (typeof record.exp !== "number" || (input.nowMs ?? Date.now()) >= record.exp) {
    return { ok: false, reason: "expired" };
  }
  if (record.epoch !== undefined && (typeof record.epoch !== "string" || record.epoch.length === 0)) {
    return { ok: false, reason: "invalid_payload" };
  }

  return {
    ok: true,
    appName: String(record.appName),
    query: record.query,
    subscriptionKey: record.subscriptionKey,
    policy: record.policy,
    scopeHash: record.scopeHash,
    userId: record.userId,
    epoch: typeof record.epoch === "string" ? record.epoch : undefined,
    issuedAt: Number(record.iat),
    expiresAt: record.exp,
  };
}
